import { ApolloQueryResult } from '@apollo/client'
import * as Apollo from 'apollo-angular'
import { Observable } from 'rxjs'
import { SimpleFilterExternalStateModel } from './simple-filter-external.state'
import { SimpleFilterStateModel } from './simple-filter.state'
import { DropdownOption } from '@wm-front/core'
import { GqlPageInfos, PaginationArgs } from '@wm-front/shared/interfaces'

export interface SimpleFilterConfiguration<T> {
  /** Explain what business data this configuration target. Used in case the config should be reusable
   * Should be specified if no key is provided */
  dataType?: SimpleFilterDataType
  /** The id of the config
   * Should be specified if no dataType is specified */
  key?: string
  /** How we want the filter to be displayed in the URL. If not specified, the key will be used */
  urlName?: string
  /** Contain data related to display, as the label or placeholder */
  display?: SimpleFilterDisplayConfiguration
  /** State the type of filter: dropdown, text, external, or period */
  type?: SimpleFilterType
  /** Should it be displayed? */
  hidden?: boolean
  /** In case of a dropdown, do we want to enable the search */
  withoutSearch?: boolean
  /** In case of a dropdown, do we want to use radio button (so only one data to be selected) instead of a checkbox? */
  radioButtonMode?: boolean
  /** In case of a dropdown, the static data to be displayed.
   * Should be specified if no datagetter is provided */
  data?: DropdownOption[]
  /** In case of a dropdown, the query which will help to provide the data.
   * Should be specified of no data is provided */
  dataGetter?: Apollo.Query<any, any>
  /** How the result of the data getter query should be converted to dropdown options */
  dropdownOptionConverter?: (data: ApolloQueryResult<T>) => DropdownOption[]
  /** How the filter can keep in its state the pagination response of the query */
  paginationConverter?: (
    data: ApolloQueryResult<T>,
    state?: SimpleFilterStateModel
  ) => GqlPageInfos
  /** How, from all of the information related to the filter state, a body can be created to be used by the data getter */
  bodyConverter?: (
    filters: any,
    state: SimpleFilterStateModel,
    externalState: SimpleFilterExternalStateModel,
    pagination?: GqlPageInfos,
    reset?: boolean,
    paginationToFetchDataWith?: PaginationArgs
  ) => any
  /** How to convert the current data to a body to prefill the selected data */
  fetchSelectedDataBodyConverter?: (
    value: any,
    state?: SimpleFilterStateModel,
    externalState?: SimpleFilterExternalStateModel
  ) => Promise<any>
  /** The default page size for the data getter */
  defaultPageSize?: number
  /** Additional filters to provide to the data getter */
  filters?: any
  /** INTERNAL ; used for responsiveness computation */
  width?: number
  /** In case of an external filter, the observable which will hold the value */
  externalFilter$?: Observable<any>
  /** How the VALUE coming from the filter should be converted */
  valueConverter?: (value: any, options?: DropdownOption[]) => any
  /**  How the Filter itself should be converted */
  filterConverter?: (value: string) => any
  /** If the search should be done locally for a dropdown */
  withoutBackendFiltering?: boolean
  /** Minimum number of characters typed before the dropdown search is triggered. Defaults to 2 (1 in Hong Kong). On a
   * text filter it has no default: when set, the text is only sent empty or at least that long */
  minSearchLength?: number
  /** Will tell what to display when the free form input is active */
  helpers?: SimpleFilterTextHelperConfiguration[]
  /** Do we want for the displayed filter to be enabled ? */
  disabled?: boolean
  /** Will tell if the filter accept an empty value. For now only used in a toggle */
  required?: boolean
  /** Classes to apply to the component */
  classes?: string[]
  /** 2026 theme: keep the filter in the bar instead of the Filtres button. Defaults to true for search, buildings and devices */
  pinned?: boolean
}

export interface SimpleFilterDisplayConfiguration {
  label: string
  placeholder?: string
  emptyResult?: string
}

export interface SimpleFilterTextHelperConfiguration {
  value: string
  description?: string
}

export enum SimpleFilterType {
  TEXT,
  DROPDOWN,
  EXTERNAL,
  PERIOD,
  TOGGLE,
}

export enum SimpleFilterDefaultDataType {
  SEARCH = 'search',
  EXTERNAL_SEARCH = 'externalSearch',
  USERS = 'users',
  MECHANICS = 'mechanics',
  ASSIGNED_MECHANIC = 'assignedMechanics',
  ASSIGNED_REPAIRER = 'assignedRepairMechanics',
  REFERENT_USERS = 'referentUsersIds',
  ASSIGNED_TECHNICIAN = 'assignedTechnicianMechanics',
  AUTHENTICATED_USER = 'accountIds',
  PERSISTENT_BUILDINGS = 'buildings',
  PERSISTENT_ACCOUNT = 'accounts',
  PERSISTENT_SUPPLY_MANAGER = 'supplyManager',
  PERSISTENT_REFERENT_USER = 'referentUser',
  PERSISTENT_OPS_MANAGER = 'opsManager',
  BRANCH_MANAGERS = 'branchManagers',
  TERRITORIES = 'territories',
  BUILDINGS = 'buildingIds',
  ACCOUNT = 'account',
  SUPPLY_MANAGER = 'supplyManagers',
  PERIOD = 'period',
}

export enum SimpleFilterRepairRequestDataType {
  URGENCY_LEVELS = 'urgencyLevels',
  CHARGEABLE = 'isChargeable',
  REPAIR_REQUEST_TYPE = 'repairRequestType',
  REPAIR_REQUEST_STATE = 'repairRequestStates',
  ONGOING_REPAIR_REQUEST_STATE = 'ongoingRepairRequestStates',
  HAS_SUPPORT_EXPERT_ASSIGNED = 'hasSupportExpertAssigned',
  HAS_REPAIR_SUPPORT_EXPERT_ASSIGNED = 'hasRepairSupportExpertAssigned',
  HAS_TECH_SUPPORT_EXPERT_ASSIGNED = 'hasTechSupportExpertAssigned',
  LINKED_TO_ISSUE = 'linkedToIssues',
  WORK_ORDER_TYPE = 'workOrderTypes',
  PROBLEM_STATE_KEYS = 'problemStateKeys',
  PROBLEM_DILAP = 'problemDilap',
}

export enum SimpleFilterDeviceDataType {
  DEVICES = 'devices',
  DEVICE_TYPES = 'deviceTypes',
  DEVICE_STATES = 'deviceStates',
  ISSUES = 'withIssue',
  STOPPED = 'stopped',
  ONBOARDING_ASSIGNED_OPS_REFERENT = 'assignedOpsReferent',
  ONBOARDING_ASSIGNED_MECHANIC = 'assignedMechanic',
  ONBOARDING_FIRST_MAINTENANCE_COMPLETED = 'firstMaintenanceCompleted',
  ONBOARDING_STICKER_INSTALLED = 'stickerInstalled',
  ONBOARDING_AUTODIALER_REPROGRAMMED = 'autodialerReprogrammed',
  ONBOARDING_LOG_CARD_INSTALLED = 'logCardInstalled',
  ONBOARDING_SECURITY_AUDIT_COMPLETED = 'securityAuditCompleted',
  ONBOARDING_HANDOVER_COMPLETED = 'handoverCompleted',
  ONBOARDING_CONTRACT_START_DATE = 'contractStartDate',
  SPECS = 'specsFilters',
}

export enum SimpleFilterAuditDataType {
  REPAIR_STATUSES = 'repairStatuses',
  AUDIT_REPORT_TYPE = 'auditReportTypes',
  QUOTE_STATUS = 'quoteStatus',
  DEFECT_TYPE = 'defectTypes',
  QUALIFICATIONS = 'qualifications',
}

export enum SimpleFilterTaskDataType {
  TYPES = 'types',
  DONE = 'isDone',
  TO_BE_DONE = 'toBeDone',
  REFERENCE = 'reference',
}

export enum SimpleFilterDocumentDataType {
  WORK_ORDER_QUOTE_STATUS = 'workOrderQuoteStatus',
}

export type SimpleFilterDataType =
  | SimpleFilterDefaultDataType
  | SimpleFilterRepairRequestDataType
  | SimpleFilterDeviceDataType
  | SimpleFilterAuditDataType
  | SimpleFilterTaskDataType
  | SimpleFilterDocumentDataType

export interface SimpleFilterChangeEvent {
  key: string
  value?: string | string[] | number[] | boolean
  shouldUpdateRoute?: boolean
}

/** The filters kept in the bar by default in the 2026 theme (PRENG-8240): search, then buildings and devices. Admin buildings stay in the navigation menu */
export const PINNED_FILTER_DATA_TYPES: SimpleFilterDataType[] = [
  SimpleFilterDefaultDataType.SEARCH,
  SimpleFilterTaskDataType.REFERENCE,
  SimpleFilterDefaultDataType.BUILDINGS,
  SimpleFilterDeviceDataType.DEVICES,
  /** The done / to do switch: it picks the view, so it stays visible (PRENG-8309) **/
  SimpleFilterTaskDataType.DONE,
  SimpleFilterTaskDataType.TO_BE_DONE,
]

export const isPinnedFilter = (
  configuration: SimpleFilterConfiguration<any>
): boolean =>
  configuration.pinned ??
  (configuration.type === SimpleFilterType.TEXT ||
    (!!configuration.dataType &&
      PINNED_FILTER_DATA_TYPES.includes(configuration.dataType)))

export const standardFilters = (
  referentUserKey = 'referentUserIds'
): SimpleFilterConfiguration<any>[] => [
  { dataType: SimpleFilterDefaultDataType.TERRITORIES },
  ...persistentFilters(referentUserKey),
  {
    dataType: SimpleFilterDefaultDataType.PERSISTENT_OPS_MANAGER,
    key: 'opsManagerUserIds',
  },
]

export const persistentFilters = (
  referentUserKey = 'referentUserIds'
): SimpleFilterConfiguration<any>[] => [
  {
    dataType: SimpleFilterDefaultDataType.PERSISTENT_BUILDINGS,
    valueConverter: (data) => data.map((d) => parseInt(d)),
  },
  {
    dataType: SimpleFilterDefaultDataType.PERSISTENT_ACCOUNT,
  },
  {
    dataType: SimpleFilterDefaultDataType.PERSISTENT_REFERENT_USER,
    key: referentUserKey,
  },
]
